import type { AvrReport, AvrReportStatus } from "../engineering/avr-report-domain.js";
import type { AvrResidualLifeSource } from "./integrity-assessment.js";

/**
 * Qual AVR alimenta a Vida Residual do Relatório de Integridade: SOMENTE AVR
 * tecnicamente válida — análise APROVADA pelo PLH ou assinada — do mesmo
 * equipamento (TAG), calculada e não desatualizada. AVR em elaboração,
 * aguardando/em análise, com correção pendente ou reprovada NUNCA alimenta a
 * Vida Residual automaticamente (a Engenharia vê a sua existência, só isso).
 * A vida remanescente é a do ponto crítico (menor VR calculada pelo motor),
 * lida como está — o resultado original do AVR nunca é modificado aqui.
 */
const VALID_STATUSES: readonly AvrReportStatus[] = ["PLH_APPROVED", "SIGNED"];
const RANK: Partial<Record<AvrReportStatus, number>> = { SIGNED: 2, PLH_APPROVED: 1 };

/** Menor vida remanescente calculada entre os pontos (anos) — nula quando nenhum ponto tem VR. */
export function avrCriticalRemainingLifeYears(avr: Pick<AvrReport, "components">): number | null {
  let min: number | null = null;
  for (const c of avr.components || []) {
    const life = c.api570?.remainingLifeYears;
    if (life === null || life === undefined || !Number.isFinite(life) || life < 0) continue;
    if (min === null || life < min) min = life;
  }
  return min;
}

export function isValidAvrForResidualLife(avr: AvrReport): boolean {
  return VALID_STATUSES.includes(avr.status) && !avr.calculationOutdated && avr.calculatedAt !== null && avrCriticalRemainingLifeYears(avr) !== null;
}

export function selectAvrForResidualLife(avrs: readonly AvrReport[], equipmentId: string | null | undefined): AvrReport | null {
  const candidates = avrs
    .filter(a => !equipmentId || String(a.equipmentId ?? "") === String(equipmentId))
    .filter(isValidAvrForResidualLife)
    .sort((a, b) => (RANK[b.status] ?? 0) - (RANK[a.status] ?? 0) || (b.updatedAt || "").localeCompare(a.updatedAt || ""));
  return candidates[0] ?? null;
}

/** Referência documental do AVR (número, revisão, data e VR em anos), pronta para a análise do RI. */
export function residualLifeSourceOf(avr: AvrReport): AvrResidualLifeSource | null {
  const years = avrCriticalRemainingLifeYears(avr);
  if (years === null) return null;
  const date = avr.signedAt || avr.plhReview?.approvedAt || avr.calculatedAt || avr.updatedAt;
  return { avrId: avr.id, avrNumber: avr.avrNumber, revision: avr.revision, date: (date || "").slice(0, 10), remainingLifeYears: years };
}
